import React, { useState, useCallback } from 'react';
import { Sun } from 'lucide-react';

export interface BrightnessStyle02Props {
  value: number;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  loading?: boolean;
  onChange: (value: number) => void;
  onChangeEnd?: (value: number) => void;
  label?: string;
  height?: number;
}

/**
 * Brightness Style 02 - Vertical Slider
 * Command: BrightnessAbsolute
 * Vertical slider bar for compact layouts
 */
export const Style02_Vertical: React.FC<BrightnessStyle02Props> = ({
  value,
  min = 0,
  max = 100,
  step = 1,
  disabled = false,
  loading = false,
  onChange,
  onChangeEnd,
  label = '亮度',
  height = 200
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);

  const displayValue = isDragging ? localValue : value;
  const percentage = ((displayValue - min) / (max - min)) * 100;

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    setLocalValue(newValue);
    onChange(newValue);
  }, [onChange]);

  const handleMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    if (onChangeEnd) {
      onChangeEnd(localValue);
    }
  }, [localValue, onChangeEnd]);

  return (
    <div className="flex flex-col items-center gap-3">
      <Sun className={`w-6 h-6 ${displayValue > 50 ? 'text-yellow-400' : 'text-slate-500'}`} />
      <div 
        className="relative bg-slate-700 rounded-full overflow-hidden"
        style={{ width: 40, height }}
      >
        <div 
          className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-yellow-500 to-yellow-300 transition-all duration-150"
          style={{ height: `${percentage}%` }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={displayValue}
          onChange={handleChange}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMouseUp}
          disabled={disabled || loading}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
          style={{ writingMode: 'vertical-lr', direction: 'rtl' } as React.CSSProperties}
        />
      </div>
      <span className="text-lg font-bold text-yellow-400">
        {loading ? '...' : `${displayValue}%`}
      </span>
      {label && <span className="text-xs text-slate-400">{label}</span>}
    </div>
  );
};

export default Style02_Vertical;
